import type { DemoUi } from "./types";

function requireElement<T extends Element>(selector: string): T {
  const element = document.querySelector<T>(selector);
  if (!element) {
    throw new Error(`Missing required element: ${selector}`);
  }
  return element;
}

export function createUi(): DemoUi {
  return {
    floatingDock: document.querySelector<HTMLElement>('[data-testid="floating-dock"]'),
    dockToggleBtn: document.querySelector<HTMLButtonElement>(
      '[data-testid="dock-toggle-btn"]'
    ),
    wasmVersion: requireElement<HTMLElement>('[data-testid="wasm-version"]'),
    tabButtons: document.querySelectorAll<HTMLButtonElement>("[data-tab-target]"),
    tabPanels: document.querySelectorAll<HTMLElement>("[data-tab-panel]"),
    relayInput: requireElement<HTMLInputElement>("#relay-url"),
    connectBtn: requireElement<HTMLButtonElement>("#connect-btn"),
    resetBtn: requireElement<HTMLButtonElement>("#reset-btn"),
    fileInput: requireElement<HTMLInputElement>("#file-input"),
    fileLabelInput: requireElement<HTMLInputElement>("#file-label"),
    uploadBtn: requireElement<HTMLButtonElement>("#upload-btn"),
    downloadBtn: requireElement<HTMLButtonElement>("#download-btn"),
    remoteTagInput: requireElement<HTMLInputElement>("#remote-tag"),
    fetchBtn: requireElement<HTMLButtonElement>("#fetch-btn"),
    datasetNamespaceInput: requireElement<HTMLInputElement>("#dataset-namespace"),
    schemaFormatInput: requireElement<HTMLInputElement>("#schema-format"),
    datasetSchemaInput: requireElement<HTMLTextAreaElement>("#dataset-schema"),
    createStreamBtn: requireElement<HTMLButtonElement>("#create-stream-btn"),
    loadSampleBtn: requireElement<HTMLButtonElement>("#load-sample-btn"),
    activeStreamTagInput: requireElement<HTMLInputElement>("#active-stream-tag"),
    remoteStreamTagInput: requireElement<HTMLInputElement>("#remote-stream-tag"),
    loadRemoteStreamBtn: requireElement<HTMLButtonElement>("#load-remote-stream-btn"),
    sliceIdInput: requireElement<HTMLInputElement>("#slice-id"),
    slicePayloadInput: requireElement<HTMLTextAreaElement>("#slice-payload"),
    appendSliceBtn: requireElement<HTMLButtonElement>("#append-slice-btn"),
    appendSecondSampleBtn: requireElement<HTMLButtonElement>(
      "#append-second-sample-btn"
    ),
    downloadSnapshotBtn: requireElement<HTMLButtonElement>("#download-snapshot-btn"),
    latestFileName: requireElement<HTMLElement>('[data-testid="latest-file-name"]'),
    latestFileTag: requireElement<HTMLElement>('[data-testid="latest-file-tag"]'),
    latestFileSize: requireElement<HTMLElement>('[data-testid="latest-file-size"]'),
    latestFileStatus: requireElement<HTMLElement>('[data-testid="latest-file-status"]'),
    connectionState: requireElement<HTMLElement>('[data-testid="connection-state"]'),
    connectionDetail: requireElement<HTMLElement>('[data-testid="connection-detail"]'),
    connectionTime: requireElement<HTMLElement>('[data-testid="connection-time"]'),
    connectionStateFiles: document.querySelector<HTMLElement>(
      '[data-testid="connection-state-files"]'
    ),
    connectionDetailFiles: document.querySelector<HTMLElement>(
      '[data-testid="connection-detail-files"]'
    ),
    connectionTimeFiles: document.querySelector<HTMLElement>(
      '[data-testid="connection-time-files"]'
    ),
    wasmStatus: requireElement<HTMLElement>('[data-testid="wasm-status"]'),
    eventLog: requireElement<HTMLUListElement>('[data-testid="event-log"]'),
    marketplaceTag: requireElement<HTMLElement>(
      '[data-testid="marketplace-stream-tag"]'
    ),
    marketplaceNamespace: requireElement<HTMLElement>(
      '[data-testid="marketplace-stream-namespace"]'
    ),
    marketplaceSlices: requireElement<HTMLElement>(
      '[data-testid="marketplace-stream-slices"]'
    ),
    marketplaceOwner: requireElement<HTMLElement>(
      '[data-testid="marketplace-stream-owner"]'
    ),
    marketplaceSnapshot: requireElement<HTMLElement>(
      '[data-testid="marketplace-snapshot"]'
    ),
    marketplaceStreamList: requireElement<HTMLUListElement>(
      '[data-testid="marketplace-stream-list"]'
    ),
    streamModal: document.querySelector<HTMLDialogElement>('[data-testid="stream-modal"]'),
    streamModalTag: requireElement<HTMLElement>('[data-testid="stream-modal-tag"]'),
    streamModalNamespace: requireElement<HTMLElement>(
      '[data-testid="stream-modal-namespace"]'
    ),
    streamModalSlices: requireElement<HTMLElement>('[data-testid="stream-modal-slices"]'),
    streamModalJson: requireElement<HTMLElement>('[data-testid="stream-modal-json"]'),
    streamModalClose: document.querySelector<HTMLButtonElement>(
      '[data-testid="stream-modal-close"]'
    ),
  };
}

function setDockCollapsed(ui: DemoUi, collapsed: boolean): void {
  if (!ui.floatingDock || !ui.dockToggleBtn) {
    return;
  }

  ui.floatingDock.dataset.collapsed = collapsed ? "true" : "false";
  document.body.dataset.dockCollapsed = collapsed ? "true" : "false";
  ui.dockToggleBtn.textContent = collapsed ? "Expand" : "Collapse";
  ui.dockToggleBtn.setAttribute("aria-expanded", collapsed ? "false" : "true");
}

export function initDock(ui: DemoUi): void {
  if (!ui.floatingDock || !ui.dockToggleBtn) {
    return;
  }

  setDockCollapsed(ui, false);
  ui.dockToggleBtn.addEventListener("click", () => {
    setDockCollapsed(ui, ui.floatingDock?.dataset.collapsed !== "true");
  });
}

function setActiveTab(ui: DemoUi, tabName: string): void {
  for (const button of ui.tabButtons) {
    const isActive = button.dataset.tabTarget === tabName;
    button.classList.toggle("is-active", isActive);
    button.setAttribute("aria-selected", isActive ? "true" : "false");
    button.tabIndex = isActive ? 0 : -1;
  }

  for (const panel of ui.tabPanels) {
    const isActive = panel.dataset.tabPanel === tabName;
    panel.classList.toggle("is-active", isActive);
    panel.hidden = !isActive;
  }
}

export function initTabs(ui: DemoUi): void {
  if (ui.tabButtons.length === 0 || ui.tabPanels.length === 0) {
    return;
  }

  setActiveTab(ui, "marketplace");
  for (const button of ui.tabButtons) {
    button.addEventListener("click", () => {
      setActiveTab(ui, button.dataset.tabTarget ?? "marketplace");
    });
  }
}
